import { AudioSvg, VideoSvg } from '@/assets/Icons';
import { CONTENT_TYPES, MULTIMEDIA_TYPES } from '@/constants/generalConstants';
import { IMAGE_SHAPES, IMAGE_SIZES } from '@/components/dorsaImage/imageTypes';
import CampaignCard from './CampaignCard';
import { FC, useMemo } from 'react';
import dict from '@/dictionaries/fa.json';
import ContentCardBadges from '@/app/community/components/ContentCardBadges';
import DorsaImage from './dorsaImage/DorsaImage';
import StatefulLink from '@/components/StatefulLink';
import useSourcePageTracker from '@/context/sourcePageTracker/useSourcePageTracker';
import getImageLink from '@/helpers/getImageLink';

export interface StreamLog {
    percentage: number;
}

export interface EntityListCardProps {
    id: string;
    className?: string;
    actionDestination: string;
    title: string;
    verticalPoster: string;
    pdpPoster?: string;
    multimediaType?: (typeof MULTIMEDIA_TYPES)[keyof typeof MULTIMEDIA_TYPES];
    seasonNumber?: number;
    episodeNumber?: number;
    specialName?: string;
    alt: string;
    streamLog?: StreamLog | null;
    cardType?: keyof typeof CONTENT_TYPES;
    isExclusive?: boolean;
    isComingSoon?: boolean;
}

function EntityListCard({
    id,
    className,
    actionDestination,
    title,
    verticalPoster,
    horizontalPoster,
    isHorizontal,
    pdpPoster,
    multimediaType,
    seasonNumber,
    episodeNumber,
    specialName,
    alt,
    streamLog = null,
    cardType = 'video',
    isExclusive = false,
    isComingSoon = false
    // hasBookmarked
}: EntityListCardProps) {
    const { sourcePageData } = useSourcePageTracker();

    const isSeries = useMemo(() => {
        return MULTIMEDIA_TYPES.series === multimediaType;
    }, [multimediaType]);

    const isEpisode = useMemo(() => {
        return MULTIMEDIA_TYPES.episode === multimediaType;
    }, [multimediaType]);

    const isSeason = useMemo(() => {
        return MULTIMEDIA_TYPES.season === multimediaType;
    }, [multimediaType]);

    let EntityIconComponent: FC;
    let properTitle = title;
    let subtitle;

    switch (cardType) {
        case CONTENT_TYPES.video:
        case CONTENT_TYPES.videoModel:
        case CONTENT_TYPES.videoSample: {
            if (!streamLog && !isSeries && !isEpisode && !isSeason) {
                EntityIconComponent = VideoSvg;
            }
            if (multimediaType === MULTIMEDIA_TYPES.episode) {
                properTitle = `${title} - ${dict.series.season + seasonNumber} - ${
                    dict.series.episode + episodeNumber
                }`;
                subtitle = specialName;
            }
            break;
        }
        case CONTENT_TYPES.audio:
        case CONTENT_TYPES.audioModel:
        case CONTENT_TYPES.audioSample: {
            EntityIconComponent = AudioSvg;
            break;
        }

        default: {
            break;
        }
    }

    const cardGeneralType =
        cardType === CONTENT_TYPES.video
            ? CONTENT_TYPES.video
            : cardType === CONTENT_TYPES.audio
              ? CONTENT_TYPES.audio
              : cardType;

    return [CONTENT_TYPES.campaignVideoTrophy, CONTENT_TYPES.campaignEvent].includes(
        cardType as string
    ) ? (
        <CampaignCard
            id={id}
            actionDestination={actionDestination}
            imageSrc={pdpPoster}
            alt={alt}
            className={
                CONTENT_TYPES.campaignVideoTrophy === cardType
                    ? 'winners-announcement-card'
                    : 'campaign-event-card'
            }
        />
    ) : (
        <StatefulLink
            href={actionDestination}
            state={sourcePageData}
            className={`small-list-card ${isSeries ? 'series-card' : ''} ${className}`}
            id={id}>
            <div
                className={`small-list-card-img-container ${cardGeneralType} ${
                    streamLog ? 'is-resumable' : ''
                }`}>
                {isSeries ? (
                    <div className="small-list-card-img-inner-container">
                        {/*todo: make these a loop*/}
                        {/*todo: is series mode, the exclusive badge is not supported yet*/}
                        {[1, 2, 3].map((imageIndex) => (
                            <DorsaImage
                                key={imageIndex}
                                className={`small-list-card-img series-img-${imageIndex} ${cardGeneralType} ${
                                    streamLog ? 'is-resumable' : ''
                                }`}
                                src={
                                    streamLog
                                        ? getImageLink(horizontalPoster)
                                        : getImageLink(verticalPoster)
                                }
                                alt={alt}
                                config={{
                                    shape:
                                        cardGeneralType === CONTENT_TYPES.audio
                                            ? IMAGE_SHAPES.SQUARE
                                            : IMAGE_SHAPES.VERTICAL,
                                    size:
                                        cardGeneralType === CONTENT_TYPES.audio
                                            ? IMAGE_SIZES.SIZE_2
                                            : IMAGE_SIZES.SIZE_1
                                }}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="small-list-card-img-inner-container">
                        <DorsaImage
                            className={`small-list-card-img ${
                                streamLog ? 'is-resumable' : ''
                            } ${cardGeneralType}`}
                            src={
                                streamLog
                                    ? getImageLink(horizontalPoster)
                                    : getImageLink(verticalPoster)
                            }
                            alt={alt}
                            config={{
                                shape:
                                    cardGeneralType === CONTENT_TYPES.audio
                                        ? IMAGE_SHAPES.SQUARE
                                        : IMAGE_SHAPES.VERTICAL,
                                size:
                                    cardGeneralType === CONTENT_TYPES.audio
                                        ? IMAGE_SIZES.SIZE_2
                                        : IMAGE_SIZES.SIZE_1
                            }}
                        />
                        <ContentCardBadges isExclusive={isExclusive} isComingSoon={isComingSoon} />
                    </div>
                )}
                {/*todo: bookmarking is temporarily omitted*/}
                {/*{cardType === 'video' && !streamLog && (*/}
                {/*  <ListItemCardBookmark id={id} hasBookmarked={hasBookmarked} />*/}
                {/*)}*/}
                {streamLog && (
                    <div className="small-list-card-resume-seekbar-outer">
                        <div
                            className="small-list-card-resume-seekbar-inner"
                            style={{ width: `${streamLog.percentage}%` }}></div>
                    </div>
                )}
            </div>
            <div className="small-list-card-title">
                {EntityIconComponent && (
                    <div className="small-list-card-title-icon-container">
                        <EntityIconComponent className="small-list-card-title-icon" />
                    </div>
                )}
                <span className="small-list-card-title-text">{properTitle}</span>
            </div>
            {subtitle && <span className="small-list-card-subtitle">{subtitle}</span>}
        </StatefulLink>
    );
}

export default EntityListCard;
